import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

type OsLicenseEdition = 'pro' | 'enterprise' | null;

type OsLicenseState = {
  hasProOrEnterprise: boolean;
  sku: string | null;
  edition: OsLicenseEdition;
  loading: boolean;
};

const ELIGIBLE_OS_SKUS = ['mapleos-pro-26', 'mapleos-enterprise-26'] as const;

function getEditionFromSku(sku: string | null): OsLicenseEdition {
  if (sku === 'mapleos-enterprise-26') {
    return 'enterprise';
  }

  if (sku === 'mapleos-pro-26') {
    return 'pro';
  }

  return null;
}

export function useOsLicense(): OsLicenseState {
  const { user } = useAuth();
  const [state, setState] = useState<OsLicenseState>({
    hasProOrEnterprise: false,
    sku: null,
    edition: null,
    loading: Boolean(user),
  });

  useEffect(() => {
    if (!user) {
      setState({
        hasProOrEnterprise: false,
        sku: null,
        edition: null,
        loading: false,
      });
      return;
    }

    let cancelled = false;

    const loadLicense = async () => {
      setState((current) => ({ ...current, loading: true }));

      const { data, error } = await supabase
        .from('mapleos_product_licenses')
        .select('sku')
        .eq('status', 'active')
        .in('sku', [...ELIGIBLE_OS_SKUS])
        .order('created_at', { ascending: false });

      if (cancelled) {
        return;
      }

      if (error) {
        setState({
          hasProOrEnterprise: false,
          sku: null,
          edition: null,
          loading: false,
        });
        return;
      }

      const ownedSkus = new Set((data ?? []).map((license: { sku: string }) => license.sku));
      const sku = ownedSkus.has('mapleos-enterprise-26')
        ? 'mapleos-enterprise-26'
        : ownedSkus.has('mapleos-pro-26')
          ? 'mapleos-pro-26'
          : null;

      setState({
        hasProOrEnterprise: Boolean(sku),
        sku,
        edition: getEditionFromSku(sku),
        loading: false,
      });
    };

    void loadLicense();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return state;
}